const MOCKUP_WIDTH = 1600;
const MOCKUP_HEIGHT = 1800;

export async function renderMerchMockupPng({
  garmentImage,
  artworkUrl,
  garmentConfig,
  view,
  placement,
  viewGeometry,
  previewSizeMode,
  printWidthRatio
}) {
  if (!garmentImage || !artworkUrl || !garmentConfig || !viewGeometry) {
    throw new Error("The current garment mockup is not ready to publish.");
  }

  const [garment, artwork] = await Promise.all([
    loadImage(garmentImage),
    loadImage(artworkUrl)
  ]);
  const canvas = document.createElement("canvas");
  canvas.width = MOCKUP_WIDTH;
  canvas.height = MOCKUP_HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("The browser could not render the garment mockup.");

  context.fillStyle = "#101010";
  context.fillRect(0, 0, canvas.width, canvas.height);
  drawContainedImage(context, garment, canvas.width, canvas.height);

  const calibration = viewGeometry.calibration;
  const visualWidth = garmentConfig.artworkLayerWidths[view] * placement.scale;
  const trueSizeWidth = calculateTrueSizeWidth(garmentConfig, view, viewGeometry.printArea, printWidthRatio);
  const layerWidthPercent = previewSizeMode === "trueSize" && trueSizeWidth
    ? trueSizeWidth
    : visualWidth;
  const artworkWidth = canvas.width * (layerWidthPercent * calibration.scaleMultiplier / 100);
  const artworkHeight = artworkWidth * (artwork.naturalHeight / artwork.naturalWidth);
  const centerX = canvas.width * ((placement.x + calibration.xOffset) / 100);
  const centerY = canvas.height * ((placement.y + calibration.yOffset) / 100);
  context.drawImage(artwork, centerX - artworkWidth / 2, centerY - artworkHeight / 2, artworkWidth, artworkHeight);

  return canvasToPngBlob(canvas);
}

function calculateTrueSizeWidth(garmentConfig, view, printArea, printWidthRatio) {
  const contentRatio = garmentConfig.artworkContentWidthRatios?.[view];
  const width = (printArea?.width * printWidthRatio) / contentRatio;
  return [printArea?.width, printWidthRatio, contentRatio, width].every((value) => Number.isFinite(value) && value > 0)
    ? width
    : null;
}

function drawContainedImage(context, image, width, height) {
  const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("A garment mockup layer could not be loaded."));
    image.src = source;
  });
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("The browser could not create the garment mockup PNG."));
    }, "image/png");
  });
}
